import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Select, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { useGetAllBranches } from 'hooks/useGetAllBranches';
import { useGetCurrentBranch } from 'hooks/useGetCurrentBranch';
import 'styles/source-branches.scss';

interface SourceBranchesProps {
  value?: number[];
  onChange?: (selectedBranches: number[]) => void;
  placeholder?: string;
}

const SourceBranches: React.FC<SourceBranchesProps> = ({ value = [], onChange, placeholder }) => {
  const { t } = useTranslation();
  const { data: branches } = useGetAllBranches();
  const { data: currentBranch } = useGetCurrentBranch();

  const defaultPlaceholder =
    placeholder || t('roomAvailabilityPage.sourceBranches.placeholders.selectBranches');

  const getInitialValue = useCallback(() => {
    if (value.length > 0) {
      return value;
    }
    if (currentBranch?.id) {
      return [currentBranch.id];
    }
    return [];
  }, [value, currentBranch]);

  const [selectedBranches, setSelectedBranches] = useState<number[]>(getInitialValue);

  useEffect(() => {
    const newValue = getInitialValue();
    setSelectedBranches(newValue);
    if (value.length === 0 && currentBranch?.id && onChange) {
      onChange(newValue);
    }
  }, [value, currentBranch, getInitialValue, onChange]);

  const sortedBranches = useMemo(() => {
    if (!branches) return [];
    return [...branches].sort((a, b) => a.name.localeCompare(b.name));
  }, [branches]);

  const handleBranchToggle = useCallback(
    (branchId: number, checked: boolean) => {
      let newSelectedBranches: number[];

      if (checked) {
        newSelectedBranches = [...selectedBranches, branchId];
      } else {
        newSelectedBranches = selectedBranches.filter(id => id !== branchId);
      }

      setSelectedBranches(newSelectedBranches);
      onChange?.(newSelectedBranches);
    },
    [selectedBranches, onChange]
  );

  const handleSelectChange = useCallback(
    (newSelectedBranches: number[]) => {
      setSelectedBranches(newSelectedBranches);
      onChange?.(newSelectedBranches);
    },
    [onChange]
  );

  const selectOptions = useMemo(() => {
    if (!branches) return [];

    return branches.map(branch => ({
      label: branch.name,
      value: branch.id
    }));
  }, [branches]);

  const dropdownRender = useCallback(
    () => (
      <div className="source-branches__dropdown">
        <div className="source-branches__branches-container">
          {sortedBranches.map((branch, index) => (
            <div key={branch.id} className="source-branches__branch-item">
              <Checkbox
                checked={selectedBranches.includes(branch.id)}
                onChange={e => handleBranchToggle(branch.id, e.target.checked)}
              >
                {`${index + 1}. ${branch.name}`}
              </Checkbox>
            </div>
          ))}
        </div>
      </div>
    ),
    [sortedBranches, selectedBranches, handleBranchToggle]
  );

  return (
    <Select
      mode="multiple"
      placeholder={defaultPlaceholder}
      value={selectedBranches}
      onChange={handleSelectChange}
      className="source-branches"
      dropdownRender={dropdownRender}
      showSearch={false}
      maxTagCount="responsive"
      options={selectOptions}
    />
  );
};

export default SourceBranches;
